import { SiweMessage } from 'siwe'
import { numberToHex } from 'web3-utils'

import { t } from '@/popup/lib/i18n'
import { UpAddress } from '@/shared/models/Profile'
import { isChrome, isManifestV3 } from '@/shared/popup'
import { hexToAssertNumber } from '@/shared/utils'

const VERSION_PATTERN = 'Version: 1' // version 1 is the only one supported currently

export type SiweErrorType = 'error' | 'warning'

export class SiweError extends Error {
  type: SiweErrorType

  constructor(message: string, type?: SiweErrorType) {
    super(message)
    this.type = type || 'error'
  }
}

/**
 * Validates SIWE message.
 *
 * @param message decoded SIWE message
 * @param origin  origin from which sign was called
 * @param chainId network chainId from which sign was called
 * @param address profile address that called the sign
 * @param isPopup true if called from a popup
 * @return SiweMessage or exception in case of error
 */
export const validateSiweMessage = async (
  message: string,
  origin: string,
  chainId: number,
  address: UpAddress,
  isPopup: boolean,
  href?: string
  // eslint-disable-next-line sonarjs/cognitive-complexity
): Promise<never | void> => {
  let siweMessage: SiweMessage

  try {
    siweMessage = new SiweMessage(message)
  } catch {
    // since SiweMessage fails on invalid version we need to check it manually
    if (!message.includes(VERSION_PATTERN)) {
      throw new SiweError(t('siwe.error.invalid_version'))
    }

    throw new SiweError(t('siwe.error.invalid_message'))
  }

  if (siweMessage.address !== address) {
    throw new SiweError(t('siwe.error.invalid_address'))
  }

  if (hexToAssertNumber(numberToHex(siweMessage.chainId)) !== chainId) {
    throw new Error(
      t('siwe.error.invalid_network', { chainId: siweMessage.chainId })
    )
  }

  if (
    siweMessage.notBefore &&
    siweMessage.expirationTime === siweMessage.notBefore
  ) {
    throw new SiweError(t('siwe.error.same_dates'))
  }

  validateSiweExpirationTime(siweMessage.expirationTime)
  validateSiweReadyTime(siweMessage.notBefore)

  // WARNINGS
  // If not at the end, they will return a warning, which will not disable the Log In button if there are errors.
  const domain = new URL(origin).host
  if (siweMessage.domain !== domain) {
    throw new SiweError(
      t('siwe.error.invalid_domain', { origin: siweMessage.domain }),
      'warning'
    )
  }

  const uri = new URL(siweMessage.uri).href
  if (isChrome()) {
    if (isManifestV3() && !isPopup) {
      const [{ url } = { url: undefined }] = await chrome.tabs.query({
        active: true,
        currentWindow: true,
      })
      if (uri !== url) {
        throw new SiweError(t('siwe.error.invalid_uri', { uri }), 'warning')
      }
    }
  } else {
    if (uri !== href) {
      throw new SiweError(t('siwe.error.invalid_uri', { uri }), 'warning')
    }
  }
}

/**
 * Initiate SiweMessage object and parse its parameters
 *
 * @param message string message based on eip-4361
 * @returns SiweMessage object or throw parse error
 */
export const parseSiweMessage = (message: string): SiweMessage => {
  const siweMessage = new SiweMessage(message)

  // remove empty strings from resources
  if (siweMessage.resources) {
    siweMessage.resources = siweMessage.resources.filter(
      resource => resource !== ''
    )
  }

  return siweMessage
}

/**
 * Check if message not before parameter is valid
 *
 * @param notBefore date string
 */
export const validateSiweReadyTime = (notBefore?: string): void | never => {
  if (notBefore && new Date(notBefore).getTime() > Date.now()) {
    throw new SiweError(t('siwe.error.not_valid_yet'))
  }
}

/**
 * Check if message expiration time parameter is valid
 *
 * @param expirationTime date string
 */
export const validateSiweExpirationTime = (
  expirationTime?: string
): void | never => {
  if (expirationTime && new Date(expirationTime).getTime() < Date.now()) {
    throw new SiweError(t('siwe.error.message_expired'))
  }
}
